import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { getCoordinates } from '../../../helpers/format-position';
import { StockSortParams } from '../../../core/services/stock.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { DEFAULT_DATE_FORMAT } from '../../../helpers/date-format.helper';
import * as moment from 'moment';
import { ExitFilterParams } from 'src/app/core/services/exit.service';

@Component({
  selector: 'app-exit-table-header',
  templateUrl: './exit-table-header.component.html',
  styleUrls: ['./exit-table-header.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: DEFAULT_DATE_FORMAT }]
})
export class ExitTableHeaderComponent implements OnInit {
  @Output() filterValues = new EventEmitter();
  minDate: Date;
  maxDate = new Date();
  lastMovedAtMinDate: Date;
  lastMovedAtMaxDate = new Date();
  showCheckIcon = {};
  appliedFilters = {};
  sortDirection = '';
  sortParams: StockSortParams;
  filterParams: ExitFilterParams;
  sortColumn = '';
  form = new FormGroup({
    startDate: new FormControl(null),
    endDate: new FormControl(null),
    sku: new FormControl(null),
    quantity: new FormControl(null),
    section: new FormControl(null),
    alias: new FormControl(null),
    position: new FormControl(null, Validators.pattern(/^[A-Za-z]{1,3}\d{1,7}$/)),
    levelName: new FormControl(null),
    lastMovedAtStartDate: new FormControl(null),
    lastMovedAtEndDate: new FormControl(null),
    productDescription: new FormControl(null),
    productName: new FormControl(null)
  });

  constructor() { }

  get isReceivedAtSaveBtnDisabled(): boolean {
    const start = this.form.get('startDate');
    const end = this.form.get('endDate');
  
    const bothDatesValid = start?.valid && end?.valid;
    const bothDatesEmpty = !start?.value && !end?.value;
  
    if (this.showCheckIcon['receivedAt']) {
      return !(bothDatesValid || bothDatesEmpty);
    } else {
      return !bothDatesValid;
    }
  }

  get isLastMovedAtSaveBtnDisabled(): boolean {
    const start = this.form.get('lastMovedAtStartDate');
    const end = this.form.get('lastMovedAtEndDate');
  
    const bothDatesValid = start?.valid && end?.valid;
    const bothDatesEmpty = !start?.value && !end?.value;
  
    if (this.showCheckIcon['lastMovedAt']) {
      return !(bothDatesValid || bothDatesEmpty);
    } else {
      return !bothDatesValid;
    }
  }

  ngOnInit(): void {
  }

  applyFilters(menu: MatMenuTrigger) {
    let x = null;
    let y = null;
    if (this.form.value.position) {
      x = getCoordinates(this.form.value.position.toUpperCase()).x;
      y = getCoordinates(this.form.value.position.toUpperCase()).y - 1;
    }
    const startDate = this.form.value.startDate ? moment(this.form.value.startDate._d).valueOf() : null;
    const endDate = this.form.value.endDate ? moment(this.form.value.endDate._d).endOf('day').valueOf() : null;
    const startDateMovedAt = this.form.value.lastMovedAtStartDate ? moment(this.form.value.lastMovedAtStartDate._d).valueOf() : null;
    const endDateMovedAt = this.form.value.lastMovedAtEndDate ? moment(this.form.value.lastMovedAtEndDate._d).endOf('day').valueOf() : null;

    this.filterParams = {
      receivedAtStartDate: startDate,
      receivedAtEndDate: endDate,
      lastMovedAtStartDate: startDateMovedAt,
      lastMovedAtEndDate: endDateMovedAt,
      quantity: this.form.value.quantity,
      sku: this.form.value.sku,
      alias: this.form.value.alias,
      section: this.form.value.section,
      levelName: this.form.value.levelName,
      productDescription: this.form.value.productDescription,
      productName: this.form.value.productName,
      x,
      y
    };

    this.removeNullKeys(this.filterParams);
    this.sortColumn = this.sortParams?.orderBy;
    this.filterValues.emit({
      filterParams: this.filterParams,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  sort(column: string, direction: string) {
    this.sortParams = null;
    this.showCheckIcon = {};
    this.sortDirection = direction;
    this.showCheckIcon[column] = true;
    this.sortParams = {
      orderBy: column,
      sortBy: direction
    };
  }

  clearFilters(menu: MatMenuTrigger, filter: string) {
    this.maxDate = new Date();
    this.minDate = null;
    this.lastMovedAtMinDate = null;
    this.lastMovedAtMaxDate = new Date();
    this.sortColumn = this.sortColumn === filter ? null : this.sortColumn;
    this.sortParams = this.sortParams?.orderBy === filter ? null : this.sortParams;
    delete this.appliedFilters?.[filter];
    delete this.showCheckIcon?.[filter];

    switch (filter) {
      case 'quantity':
        this.form.get('quantity').reset();
        delete this.filterParams?.['quantity'];
        break;
      case 'receivedAt':
        this.form.get('startDate').reset();
        this.form.get('endDate').reset();
        delete this.filterParams?.['receivedAtStartDate'];
        delete this.filterParams?.['receivedAtEndDate'];
        break;
      case 'sku':
        this.form.get('sku').reset();
        delete this.filterParams?.['sku'];
        break;
      case 'levelName':
        this.form.get('levelName').reset();
        delete this.filterParams?.['levelName'];
        break;
      case 'section':
        this.form.get('section').reset();
        delete this.filterParams?.['section'];
        break;
      case 'position':
        this.form.get('position').reset();
        delete this.filterParams?.['x'];
        delete this.filterParams?.['y'];
        break;
      case 'alias':
        this.form.get('alias').reset();
        delete this.filterParams?.['alias'];
        break;
      case 'lastMovedAt':
        this.form.get('lastMovedAtStartDate').reset();
        this.form.get('lastMovedAtEndDate').reset();
        delete this.filterParams?.['lastMovedAtStartDate'];
        delete this.filterParams?.['lastMovedAtEndDate'];
        break;
      case 'productDescription':
        this.form.get('productDescription').reset();
        delete this.filterParams?.['productDescription'];
        break;
      case 'productName':
        this.form.get('productName').reset();
        delete this.filterParams?.['productName'];
        break;
    }

    this.filterValues.emit({
      filterParams: this.filterParams,
      sortParams: this.sortParams
    });
    menu.closeMenu();
  }

  removeNullKeys(params) {
    Object.keys(params).forEach(key => {
      if (params[key] === null) {
        delete params[key];
      } else {
        this.setAppliedFilters(key);
      }
    });
  }

  setAppliedFilters(name: string) {
    switch (name) {
      case 'receivedAtStartDate':
      case 'receivedAtEndDate':
        this.appliedFilters['receivedAt'] = true;
        break;
      case 'lastMovedAtStartDate':
      case 'lastMovedAtEndDate':
        this.appliedFilters['lastMovedAt'] = true;
        break;
      case 'x':
      case 'y':
        this.appliedFilters['position'] = true;
        break;
      default:
        this.appliedFilters[name] = true;
        break;
    }
  }

  onReceivedAtStartDateChange() {
    this.minDate = this.form.value.startDate._d;
  }

  onReceivedAtEndDateChange() {
    this.maxDate = this.form.value.endDate._d;
  }

  onLastMovedAtStartDateChange() {
    this.lastMovedAtMinDate = this.form.value.lastMovedAtStartDate._d;
  }

  onLastMovedAtEndDateChange() {
    this.lastMovedAtMaxDate = this.form.value.lastMovedAtEndDate._d;
  }
}
